// @ts-strict-ignore
import React from 'react';
import pick from 'lodash/pick';
import { Button, ButtonVariant } from '@usealloy/component-library';

import {
  StyledCSVWrapper,
} from '~/entities/evaluations/transaction/history/TransactionHistoryStyles';
import ExportCSVIcon from '~/dashboard/components/ExportCSVIcon';

import { getNumberWithDecimal } from '~/helpers/i18n';
import { snakeToCaps } from '~/helpers/formatting';
import initiateCSVDownload from '~/helpers/csv';

import { DEFAULT_COLUMNS } from '~/entities/evaluations/transaction/history/constants';

type VisibleColumn = {
  name: string;
  prop: string;
  orderBy: boolean;
};

type ExportCSVProps = {
  title: string;
  columns: Array<VisibleColumn>;
  transactions: Array<any>;
  useCLButton?: boolean;
};

const ExportCSV: React.FC<ExportCSVProps> = ({
  title, columns, transactions, useCLButton,
}) => {
  const getDollarAmount = trx => ({
    ...trx,
    amount: trx.amount ? getNumberWithDecimal(trx.amount, {
      alphabeticCurrencyCode: trx?.currency_code,
    }) : null,
  });
  const filterBy = cols => trx => pick(trx, cols);
  const orderBy = cols => trx => cols.map(column => trx[column]);

  /**
   * Returns an object with updated transactions and columns
   * To spread the transaction[prop] object into each transaction row
   * and get the additional column names needed for each property.
   * Purpose: to show each key within prop as a column rather than have one blob column
    @param trxs - array of transactions
    @param cols - array of column names
    @param prop - property name in a transaction
  * */
  const handleSpreadObjPropIntoTrx = (trxs, cols, prop) => {
    const setOfCols = new Set([...cols]);
    const updatedTransactions = trxs.map(trx => {
      const propValue = trx[prop];
      const updatedTrx = { ...trx };
      delete updatedTrx[prop];

      const renamedKeys = {};
      if (propValue) {
        Object.entries(propValue).forEach(([key, value]) => {
          const newKey = `${prop}.${key}`;
          renamedKeys[newKey] = value?.toString();
          setOfCols.add(newKey);
        });
      }

      return {
        ...updatedTrx,
        ...renamedKeys,
      };
    });
    setOfCols.delete(prop);

    return {
      transactions: updatedTransactions as Array<object>,
      columns: Array.from(setOfCols) as Array<string>,
    };
  };

  const formatValue = value => {
    if (!value) return '-';
    if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
    return value;
  };

  const buildFinalString = (orderedColumns, orderedTransactions) => {
    const header = orderedColumns.reduce((partialHeader, currentHeader) => `${partialHeader},${snakeToCaps(currentHeader)}`, '');
    const rows = orderedTransactions.reduce((partialCsv, transactionValues) => {
      const valueString = transactionValues.reduce((partialRowString, value) => `${partialRowString},${formatValue(value)}`, '');
      return `${partialCsv} ${valueString}\n`;
    }, '');

    return `${header}\n ${rows}`;
  };

  const handleExportCSV = () => {
    if (transactions.length === 0) return;

    const selectedCols = columns
      .map(({ prop }) => prop)
      .filter(prop => !DEFAULT_COLUMNS.hiddenFromCSV.some(colValue => colValue === prop));

    let formattedTransactions = transactions;
    let formattedColumns = selectedCols;
    if (selectedCols.includes('metadata')) {
      const updatedResult = handleSpreadObjPropIntoTrx(
        transactions, selectedCols, 'metadata',
      );
      formattedColumns = updatedResult.columns;
      formattedTransactions = updatedResult.transactions;
    }

    const orderedColumns = formattedColumns.includes('product_name')
      ? ['product_name', ...formattedColumns.filter(col => col !== 'product_name')] : formattedColumns;

    const orderedTransactions = formattedTransactions
      .map(transaction => [
        getDollarAmount,
        filterBy(orderedColumns),
        orderBy(orderedColumns),
      ].reduce((transactionData, operation) => operation(transactionData), transaction));

    const finalCSVString = buildFinalString(orderedColumns, orderedTransactions);
    initiateCSVDownload(title, finalCSVString);
  };

  return useCLButton
    ? (
      <Button
        variant={ButtonVariant.tertiary}
        onClick={handleExportCSV}
        disabled={transactions.length === 0}
        data-dd-action-name="alloydd-export-csv-btn"
      >
        Export CSV
      </Button>
    ) : (
      <StyledCSVWrapper
        onClick={handleExportCSV}
        data-dd-action-name="alloydd-export-csv-btn"
        role="button"
        aria-label="Export CSV"
      >
        <ExportCSVIcon disabled={transactions.length === 0} />
      </StyledCSVWrapper>
    );
};

export default ExportCSV;
